import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, Globe, FileText, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AVAILABLE_EVENTS = [
  { value: "client.created", label: "Client Created" },
  { value: "client.updated", label: "Client Updated" },
  { value: "lead.created", label: "Lead Created" },
  { value: "lead.updated", label: "Lead Updated" }
];

export default function WebhookSettings({ user }) {
  const [webhooks, setWebhooks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    method: "POST",
    events: [],
    is_active: true,
    secret_key: ""
  });
  
  // Logs dialog states
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [currentWebhook, setCurrentWebhook] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const response = await api.get("/webhooks");
      setWebhooks(response.data);
    } catch (error) {
      toast.error("Failed to fetch webhooks");
    }
  };

  const handleOpenDialog = (webhook = null) => {
    if (webhook) {
      setEditingWebhook(webhook);
      setFormData({
        name: webhook.name,
        url: webhook.url,
        method: webhook.method,
        events: webhook.events,
        is_active: webhook.is_active,
        secret_key: webhook.secret_key || ""
      });
    } else {
      setEditingWebhook(null);
      setFormData({
        name: "",
        url: "",
        method: "POST",
        events: [],
        is_active: true,
        secret_key: ""
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.url) {
      toast.error("Name and URL are required");
      return;
    }

    if (formData.events.length === 0) {
      toast.error("Please select at least one event");
      return;
    }

    try {
      if (editingWebhook) {
        await api.put(`/webhooks/${editingWebhook.id}`, formData);
        toast.success("Webhook updated successfully");
      } else {
        await api.post("/webhooks", formData);
        toast.success("Webhook created successfully");
      }
      
      setDialogOpen(false);
      fetchWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save webhook");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this webhook?")) return;

    try {
      await api.delete(`/webhooks/${id}`);
      toast.success("Webhook deleted successfully");
      fetchWebhooks();
    } catch (error) {
      toast.error("Failed to delete webhook");
    }
  };

  const handleToggleActive = async (webhook) => {
    try {
      await api.put(`/webhooks/${webhook.id}`, {
        is_active: !webhook.is_active
      });
      toast.success(`Webhook ${!webhook.is_active ? "enabled" : "disabled"}`);
      fetchWebhooks();
    } catch (error) {
      toast.error("Failed to toggle webhook status");
    }
  };

  const handleViewLogs = async (webhook) => {
    setCurrentWebhook(webhook);
    setLogsDialogOpen(true);
    setLoadingLogs(true);
    
    try {
      const response = await api.get(`/webhooks/${webhook.id}/logs`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Failed to fetch webhook logs");
      console.error(error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return "N/A";
    }
  };

  const getStatusBadge = (log) => {
    if (log.success) {
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Success
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Failed
        </Badge>
      );
    }
  };



  const toggleEvent = (eventValue) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventValue)
        ? prev.events.filter(e => e !== eventValue)
        : [...prev.events, eventValue]
    }));
  };

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-md3-display-small text-md3-on-surface">Webhook Settings</h1>
          <p className="text-md3-on-surface-variant mt-1">Manage webhooks to send client and lead updates to external systems</p>
        </div>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-md3-title-large text-md3-on-surface ">Webhooks</CardTitle>
              <CardDescription>Configure webhooks to push data to external applications</CardDescription>
            </div>
            <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Webhook
            </Button>
          </CardHeader>
          <CardContent>
            {webhooks.length === 0 ? (
              <div className="text-center py-12 text-md3-on-surface-variant">
                <Globe className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No webhooks configured</p>
                <p className="text-md3-body-small mt-2">Add a webhook to start sending data to external systems</p>
              </div>
            ) : (
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Stats</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook.id}>
                      <TableCell className="font-medium">{webhook.name}</TableCell>
                      <TableCell className="max-w-xs truncate">{webhook.url}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{webhook.method}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {webhook.events.map((event) => (
                            <Badge key={event} variant="secondary" className="text-xs">
                              {event.replace(".", " ")}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={webhook.is_active}
                            onCheckedChange={() => handleToggleActive(webhook)}
                          />
                          <span className={webhook.is_active ? "text-green-600" : "text-gray-400"}>
                            {webhook.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-md3-body-small">
                          <span className="text-green-600">{(webhook.total_calls || 0) - (webhook.failed_calls || 0)}</span>
                          <span className="text-gray-400"> / </span>
                          <span className="text-red-600">{webhook.failed_calls || 0}</span>
                        </div>
                        <div className="text-xs text-md3-on-surface-variant">success / failed</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                            onClick={() => handleViewLogs(webhook)}
                            title="View Logs"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button size="sm"
                            variant="outline"
                            className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                            onClick={() => handleOpenDialog(webhook)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm"
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                            onClick={() => handleDelete(webhook.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Webhook Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editingWebhook ? "Edit Webhook" : "Add New Webhook"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Configure webhook to send client/lead updates to external systems
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-md3-label-large text-gray-700 font-medium">Webhook Name *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="name"
                  placeholder="MSL Application Webhook"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="url" className="text-md3-label-large text-gray-700 font-medium">Webhook URL *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="url"
                  placeholder="https://example.com/api/webhook/milekal-pulse"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="method" className="text-md3-label-large text-gray-700 font-medium">HTTP Method</Label>
                <Select value={formData.method} onValueChange={(value) => setFormData({ ...formData, method: value })}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="PUT">PUT</SelectItem>
                    <SelectItem value="GET">GET</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Trigger Events *</Label>
                <div className="space-y-2 border rounded-md3-md p-4">
                  {AVAILABLE_EVENTS.map((event) => (
                    <div key={event.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={event.value}
                        checked={formData.events.includes(event.value)}
                        onCheckedChange={() => toggleEvent(event.value)}
                      />
                      <label
                        htmlFor={event.value}
                        className="text-md3-body-small font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {event.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="secret_key" className="text-md3-label-large text-gray-700 font-medium">Secret Key (Optional)</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="secret_key"
                  type="password"
                  placeholder="Optional webhook secret for authentication"
                  value={formData.secret_key}
                  onChange={(e) => setFormData({ ...formData, secret_key: e.target.value })}
                />
                <p className="text-xs text-md3-on-surface-variant">Secret will be sent in X-Webhook-Secret header</p>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active" className="text-md3-label-large text-gray-700 font-medium">Enable webhook</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editingWebhook ? "Update Webhook" : "Create Webhook"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

        {/* Logs Dialog */}
        <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Webhook Call Logs</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {currentWebhook && `Recent webhook calls for "${currentWebhook.name}" (Last 100)`}
              </DialogDescription>
            </DialogHeader>
            
            {loadingLogs ? (
              <div className="text-center py-8">Loading logs...</div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-md3-on-surface-variant">
                No logs found. This webhook hasn't been triggered yet.
              </div>
            ) : (
              <div className="space-y-6">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Response Time</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-md3-body-small">
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.event_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(log)}
                        </TableCell>
                        <TableCell>
                          {log.response_time_ms ? (
                            <span className={log.response_time_ms > 1000 ? "text-orange-600" : "text-md3-on-surface-variant"}>
                              {log.response_time_ms}ms
                            </span>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.success ? (
                            <div className="text-md3-body-small text-md3-on-surface-variant">
                              {log.status_code && `HTTP ${log.status_code}`}
                            </div>
                          ) : (
                            <div className="text-md3-body-small">
                              <div className="text-red-600 font-medium">
                                {log.status_code ? `HTTP ${log.status_code}` : "Error"}
                              </div>
                              <div className="text-xs text-md3-on-surface-variant mt-1 max-w-xs truncate">
                                {log.error_message || "Unknown error"}
                              </div>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Summary Stats */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-md3-headline-large text-md3-on-surface-variant">{logs.length}</div>
                      <div className="text-md3-body-small text-md3-on-surface-variant">Total Calls</div>
                    </div>
                    <div>
                      <div className="text-md3-headline-large text-green-600">
                        {logs.filter(l => l.success).length}
                      </div>
                      <div className="text-md3-body-small text-md3-on-surface-variant">Successful</div>
                    </div>
                    <div>
                      <div className="text-md3-headline-large text-red-600">
                        {logs.filter(l => !l.success).length}
                      </div>
                      <div className="text-md3-body-small text-md3-on-surface-variant">Failed</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end pt-4 border-t">
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setLogsDialogOpen(false)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>


    </Layout>
  );
}
